import { DeleteAccountSheet } from "../components/DeleteAccountSheet.tsx";
import { ScreenHeader } from "../components/ScreenHeader.tsx";
import { UserName } from "../components/UserName.tsx";
import { RemoveAdsCard } from "../components/RemoveAdsCard.tsx";
import { Sheet } from "../components/Sheet.tsx";
import { SegmentedControl } from "../components/SegmentedControl.tsx";
import { PasswordInput } from "../components/PasswordInput.tsx";
import { SoundSettingsSheet } from "../components/SoundSettingsSheet.tsx";
import { useSoundPreference } from "../lib/useSoundPreference.ts";
import { nextUsernameChangeAt } from "@mk/shared";
import { useSession } from "../lib/session.ts";
import type { RegisterBackHandler } from "../lib/navigation.ts";
import { applyTheme, getStoredTheme, type Theme } from "../lib/theme.ts";
import {
  getStoredKeyboardActionOrder,
  storeKeyboardActionOrder,
  type KeyboardActionOrder,
} from "../lib/keyboardLayout.ts";
import { useEffect, useState } from "react";
import { messages } from "../i18n/index.ts";
import { APP_EDITION } from "../lib/edition.ts";
import { isNativeApp } from "../lib/native.ts";
import { openAdPrivacyOptions } from "../lib/adGate.ts";
import { ApiError } from "../lib/api.ts";
import { combinedDuelRecord } from "../features/stats/duelRecord.ts";

interface ProfileScreenProps {
  onBack: () => void;
  registerBackHandler: RegisterBackHandler;
}

const THEME_LABEL: Record<Theme, string> = {
  system: messages.profile.themes.system,
  dark: messages.profile.themes.dark,
  light: messages.profile.themes.light,
};

export function ProfileScreen({ onBack, registerBackHandler }: ProfileScreenProps) {
  const { user, stats, account, logout, renameUsername, changePassword, usernameChangedAt } = useSession();
  const [newUsername, setNewUsername] = useState("");
  const [nameBusy, setNameBusy] = useState(false);
  const [nameNotice, setNameNotice] = useState<string | null>(null);
  const [nameOpen, setNameOpen] = useState(false);
  const nextChangeAt = nextUsernameChangeAt(usernameChangedAt);
  const canChange = nextChangeAt === null || Date.now() >= nextChangeAt;
  const duelRecord = combinedDuelRecord(stats);
  const [theme, setTheme] = useState<Theme>(getStoredTheme);
  const [keyboardActionOrder, setKeyboardActionOrder] = useState<KeyboardActionOrder>(
    getStoredKeyboardActionOrder,
  );
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [soundOpen, setSoundOpen] = useState(false);
  const [passwordOpen, setPasswordOpen] = useState(false);
  const [currentPassword, setCurrentPassword] = useState("");
  const [nextPassword, setNextPassword] = useState("");
  const [nextPasswordAgain, setNextPasswordAgain] = useState("");
  const [passwordBusy, setPasswordBusy] = useState(false);
  const [passwordNotice, setPasswordNotice] = useState<string | null>(null);
  const [passwordDone, setPasswordDone] = useState(false);
  const passwordMismatch = nextPasswordAgain.length > 0 && nextPassword !== nextPasswordAgain;
  function closePasswordSheet() {
    if (passwordBusy) return;
    setPasswordOpen(false);
    setCurrentPassword("");
    setNextPassword("");
    setNextPasswordAgain("");
    setPasswordNotice(null);
  }
  const sound = useSoundPreference();
  function closeNameSheet() {
    if (nameBusy) return;
    setNameOpen(false);
    setNameNotice(null);
    setNewUsername("");
  }
  // Açık panel varken geri tuşu önce paneli kapatsın.
  useEffect(() => {
    if (!deleteOpen && !nameOpen && !soundOpen && !passwordOpen) return;
    return registerBackHandler(() => {
      if (soundOpen) {
        setSoundOpen(false);
      } else if (passwordOpen) {
        closePasswordSheet();
      } else if (nameOpen) {
        if (!nameBusy) {
          setNameOpen(false);
          setNameNotice(null);
          setNewUsername("");
        }
      } else setDeleteOpen(false);
      return "block";
    });
  }, [deleteOpen, nameBusy, nameOpen, passwordOpen, registerBackHandler, soundOpen]);

  return (
    <div className="app">
      <ScreenHeader title={user ? <UserName user={user} /> : messages.profile.title} onBack={onBack} />

      <main className="overflow-y-auto px-4 py-5">
        <div className="mx-auto flex w-full max-w-md flex-col gap-6">
          <section>
            <h3 className="section-title">{messages.profile.duelRecordTitle}</h3>
            <p className="text-sm text-muted">{messages.profile.duelRecord(duelRecord.wins, duelRecord.losses, duelRecord.draws)}</p>
          </section>
          <section>
            <h3 className="section-title">{messages.profile.appearance}</h3>
            <div className="flex flex-col gap-3">
              <SegmentedControl
                label={messages.profile.themeLabel}
                options={(["system", "dark", "light"] as const).map((value) => ({ value, label: THEME_LABEL[value] }))}
                value={theme}
                onChange={(next) => {
                  applyTheme(next);
                  setTheme(next);
                }}
              />
              <SegmentedControl
                label={messages.profile.keyboardLabel}
                options={(["default", "swapped"] as const).map((value) => ({
                  value,
                  label: messages.profile.keyboardOptions[value],
                }))}
                value={keyboardActionOrder}
                onChange={(next) => {
                  storeKeyboardActionOrder(next);
                  setKeyboardActionOrder(next);
                }}
              />
              <button
                type="button"
                className="btn-secondary w-full"
                onClick={() => setSoundOpen(true)}
                aria-haspopup="dialog"
              >
                {sound.enabled ? messages.profile.soundRow(sound.volume) : messages.sound.off}
              </button>
            </div>
          </section>

          <RemoveAdsCard />

          {isNativeApp() && <button type="button" className="btn-secondary" onClick={() => { void openAdPrivacyOptions(); }}>{messages.profile.adPrivacy}</button>}

          <section className="flex flex-col gap-2">
            <button type="button" className="btn-secondary" onClick={() => setNameOpen(true)}>
              {messages.profile.changeUsername}
            </button>
            {account?.hasPassword && (
              <button type="button" className="btn-secondary" onClick={() => { setPasswordDone(false); setPasswordOpen(true); }}>
                {messages.profile.changePassword}
              </button>
            )}
            {passwordDone && <p role="status" className="text-center text-sm text-mint">{messages.profile.passwordChanged}</p>}
            <button
              type="button"
              className="btn-secondary"
              onClick={() => {
                void logout();
                onBack();
              }}
            >
              {messages.profile.logout}
            </button>

            <button
              type="button"
              className="btn-danger"
              onClick={() => setDeleteOpen(true)}
            >
              {messages.profile.delete}
            </button>
            <p className="text-center text-xs leading-relaxed text-dim">
              {messages.profile.deleteHint}
            </p>

            {/* Sayfa uygulamadan bağımsız servis ediliyor; yeni sekmede açılır. */}
            <p className="mt-1 text-center text-xs text-dim">
              <a
                className="text-link"
                href={APP_EDITION === "en" ? "/privacy" : "/gizlilik"}
                target="_blank"
                rel="noreferrer"
              >
                {messages.profile.privacy}
              </a>
            </p>
          </section>
        </div>
      </main>

      <Sheet open={nameOpen} title={messages.profile.changeUsername} placement="center"
        onClose={closeNameSheet}>
        <form className="flex flex-col gap-3" onSubmit={(event) => {
          event.preventDefault();
          if (!canChange || nameBusy) return;
          setNameBusy(true);
          setNameNotice(null);
          void renameUsername(newUsername).then(() => {
            setNameOpen(false);
            setNewUsername("");
          }).catch((error: unknown) => {
            setNameNotice(error instanceof ApiError ? error.message : messages.auth.connectionFailed);
          }).finally(() => setNameBusy(false));
        }}>
          <label className="field">
            <span>{messages.profile.newUsername}</span>
            <input type="text" value={newUsername} onChange={(event) => setNewUsername(event.target.value)}
              minLength={3} maxLength={20} autoComplete="username" autoCapitalize="none"
              disabled={!canChange || nameBusy} required />
            <small>{messages.auth.usernameHint}</small>
          </label>
          {!canChange && nextChangeAt && <p className="text-sm text-muted">{messages.profile.nextUsernameChange(new Date(nextChangeAt).toLocaleDateString(APP_EDITION === "tr" ? "tr-TR" : "en-US"))}</p>}
          {nameNotice && <p role="alert" className="text-sm text-flame">{nameNotice}</p>}
          <button type="submit" className="btn-secondary" disabled={!canChange || nameBusy}>{messages.profile.saveUsername}</button>
        </form>
      </Sheet>

      <Sheet open={passwordOpen} title={messages.profile.changePassword} placement="center"
        onClose={closePasswordSheet}>
        <form className="flex flex-col gap-3" onSubmit={(event) => {
          event.preventDefault();
          if (passwordBusy || passwordMismatch) return;
          setPasswordBusy(true);
          setPasswordNotice(null);
          void changePassword(currentPassword, nextPassword).then(() => {
            setPasswordOpen(false);
            setPasswordDone(true);
            setCurrentPassword("");
            setNextPassword("");
            setNextPasswordAgain("");
          }).catch((error: unknown) => {
            setPasswordNotice(error instanceof ApiError ? error.message : messages.auth.connectionFailed);
          }).finally(() => setPasswordBusy(false));
        }}>
          <label className="field">
            <span>{messages.profile.currentPassword}</span>
            <PasswordInput value={currentPassword} onChange={(event) => setCurrentPassword(event.target.value)}
              autoComplete="current-password" disabled={passwordBusy} required />
          </label>
          <label className="field">
            <span>{messages.profile.newPassword}</span>
            <PasswordInput value={nextPassword} onChange={(event) => setNextPassword(event.target.value)}
              autoComplete="new-password" minLength={8} disabled={passwordBusy} required />
            <small>{messages.auth.passwordMin}</small>
          </label>
          <label className="field">
            <span>{messages.profile.repeatNewPassword}</span>
            <PasswordInput value={nextPasswordAgain} onChange={(event) => setNextPasswordAgain(event.target.value)}
              autoComplete="new-password" aria-invalid={passwordMismatch || undefined} disabled={passwordBusy} required />
            {passwordMismatch && <small className="text-flame">{messages.auth.passwordMismatch}</small>}
          </label>
          {passwordNotice && <p role="alert" className="text-sm text-flame">{passwordNotice}</p>}
          <button type="submit" className="btn-secondary" disabled={passwordBusy || passwordMismatch}>
            {messages.profile.savePassword}
          </button>
        </form>
      </Sheet>

      <SoundSettingsSheet
        open={soundOpen}
        volume={sound.volume}
        onVolumeChange={sound.setVolume}
        onToggleMuted={() => sound.setEnabled(!sound.enabled)}
        onClose={() => setSoundOpen(false)}
      />

      <DeleteAccountSheet
        open={deleteOpen}
        onClose={() => setDeleteOpen(false)}
        onDeleted={() => {
          setDeleteOpen(false);
          onBack();
        }}
      />
    </div>
  );
}
